import type { ToastVariant } from "@/components";

/**
 * FAIL-484 — pure decision logic for the post-checkout return, split out of
 * IngestEditor's mount effect so it's unit-testable without rendering the
 * whole editor (matches the firstMap.ts / autoMake.ts pattern in this dir).
 */

/** The Stripe-hosted checkout's success redirect: `/?checkout=success&session_id=...`. */
export function checkoutReturnSessionId(search: string): string | null {
  const params = new URLSearchParams(search);
  if (params.get("checkout") !== "success") return null;
  return params.get("session_id");
}

/** The same query string with `checkout`/`session_id` stripped, for history.replaceState. */
export function stripCheckoutReturnParams(search: string): string {
  const params = new URLSearchParams(search);
  params.delete("checkout");
  params.delete("session_id");
  return params.toString();
}

export interface CheckoutResumeResponse {
  paid?: boolean;
  entitled?: boolean;
  plan_tier?: string | null;
  resumed_action?: string | null;
}

export interface CheckoutReturnToast {
  variant: ToastVariant;
  title: string;
  message: string;
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** The confirmation (or apology) toast for a /api/billing/resume outcome. */
export function buildCheckoutReturnToast(data: CheckoutResumeResponse | null): CheckoutReturnToast {
  if (!data) {
    return {
      variant: "warning",
      title: "Couldn't confirm your payment",
      message: "Your payment may still have gone through — check your email for a receipt.",
    };
  }
  if (!data.paid) {
    return {
      variant: "warning",
      title: "Checkout didn't complete",
      message: "If you were charged, contact support — otherwise feel free to try again.",
    };
  }
  const tierLabel = data.plan_tier ? capitalize(data.plan_tier) : null;
  return {
    variant: "success",
    title: tierLabel ? `You're on ${tierLabel} now` : "Payment received",
    message: data.resumed_action ? "Unlocking everything — one moment…" : "Your plan is active.",
  };
}
